import { useEffect, useState } from "react";
import { toast } from "sonner";
import { teacherApi } from "@/lib/api";
import { addJustFixed } from "@/lib/justFixed";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ConfirmDialog";
import { Loader2, Wand2, Ruler, Database, Trash2, PlayCircle, ChevronDown, ChevronUp, Sparkles } from "lucide-react";

const STATUS_COLOR = {
  ok: "bg-[#D1FAE5] text-[#065F46]",
  thin: "bg-[#FEF3C7] text-[#92400E]",
  gap: "bg-[#FEE2E2] text-[#991B1B]",
  misplaced: "bg-[#E9D5FF] text-[#6B21A8]",
};

const STATUS_LABEL = {
  ok: "OK",
  thin: "cienki",
  gap: "brak",
  misplaced: "spoza",
};

export default function CurationTab() {
  const askConfirm = useConfirm();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null); // "fix-units" | "normalize" | "gap-fill" | ...
  const [showDupes, setShowDupes] = useState(false);
  const [gapProgress, setGapProgress] = useState(null); // {done, total, current}
  const [misplaced, setMisplaced] = useState(null);
  // Bulk synonym sweep state
  const [synProgress, setSynProgress] = useState(null); // {processed, total, withSuggestions}
  const [pendingSyn, setPendingSyn] = useState(null); // {items: [{qid, selected: [{text, selected}]}]}
  // TEST topic cleanup — orphaned pytest fixtures that admin wants gone
  const [testTopics, setTestTopics] = useState(null); // {topics: [{topic, count, scopes}], total_questions}

  const loadTestTopics = async () => {
    try {
      const { data } = await teacherApi.get("/admin/curation/test-topics-preview");
      setTestTopics(data);
    } catch (e) {
      toast.error("Nie udało się pobrać listy pytań testowych");
    }
  };

  const purgeTestTopics = async (topics = null) => {
    const scope = topics
      ? `temat „${topics[0]}" (${testTopics?.topics?.find((t) => t.topic === topics[0])?.count || 0} pytań)`
      : `WSZYSTKIE pytania TEST_* (${testTopics?.total_questions || 0} pytań w ${testTopics?.topics?.length || 0} tematach)`;
    const ok = await askConfirm({
      title: "Usunąć pytania testowe?",
      description: `Zostaną miękko usunięte (deleted_at). Zakres: ${scope}. Operacja nieodwracalna z panelu, ale baza zachowuje dokumenty.`,
      confirmText: "Usuń",
    });
    if (!ok) return;
    setBusy("delete-test-topics");
    try {
      const { data } = await teacherApi.post(
        "/admin/curation/delete-test-topics",
        topics ? { topics } : {},
      );
      toast.success(`Usunięto ${data.deleted} pytań testowych`);
      await loadTestTopics();
      await loadReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd usuwania");
    } finally { setBusy(null); }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const { data } = await teacherApi.get("/admin/curation/coverage");
      setReport(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się pobrać raportu");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadReport(); loadTestTopics(); }, []);

  const doBackup = async () => {
    const ok = await askConfirm({
      title: "Wykonać kopię bazy?",
      description: "Utworzy pełny snapshot kolekcji przed operacją.",
      confirmText: "Backup",
    });
    if (!ok) return;
    setBusy("backup");
    try {
      await teacherApi.post("/admin/curation/backup");
      toast.success("Backup zakończony");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backup nieudany");
    } finally { setBusy(null); }
  };

  const doFixUnits = async (dry) => {
    setBusy("fix-units");
    try {
      const { data } = await teacherApi.post("/admin/curation/fix-units", { dry_run: dry });
      if (dry) {
        const ok = await askConfirm({
          title: `Znaleziono ${data.questions_changed} pytań do poprawy`,
          description: `Zmiany w polach: text (${data.fields_changed.text}), correct (${data.fields_changed.correct}), explanation (${data.fields_changed.explanation}), options (${data.fields_changed.options}). Zastosować?`,
          confirmText: "Zastosuj",
        });
        if (!ok) return;
        const { data: applied } = await teacherApi.post("/admin/curation/fix-units", { dry_run: false });
        toast.success(`Zastosowano ${applied.questions_changed} zmian`);
        loadReport();
      } else {
        toast.success(`Zmian: ${data.questions_changed}`);
        loadReport();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fix-units nieudany");
    } finally { setBusy(null); }
  };

  const doNormalize = async () => {
    setBusy("normalize");
    try {
      const { data } = await teacherApi.post("/admin/curation/normalize-topics", { dry_run: true });
      if (!data.clusters?.length) {
        toast.info("LLM nie znalazł klastrów do znormalizowania");
        return;
      }
      const totalMembers = data.clusters.reduce((a, c) => a + (c.members?.length || 0), 0);
      const ok = await askConfirm({
        title: `${data.clusters.length} klastrów, ${totalMembers} tematów do znormalizowania`,
        description: `Przykład: "${data.clusters[0].members.join(", ")}" → "${data.clusters[0].canonical}". Zastosować wszystkie?`,
        confirmText: "Zastosuj",
      });
      if (!ok) return;
      const { data: applied } = await teacherApi.post("/admin/curation/normalize-topics", { dry_run: false });
      toast.success(`Znormalizowano ${applied.moved} pytań (${applied.clusters.length} klastrów)`);
      loadReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Normalizacja nieudana");
    } finally { setBusy(null); }
  };

  const loadMisplaced = async () => {
    setBusy("misplaced");
    try {
      const { data } = await teacherApi.get("/admin/curation/misplaced?limit=200");
      setMisplaced(data);
      if (!data.count) toast.info("Brak pytań poza podstawą programową");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd");
    } finally { setBusy(null); }
  };

  const deleteMisplacedBatch = async () => {
    if (!misplaced?.items?.length) return;
    const ok = await askConfirm({
      title: `Usunąć ${misplaced.items.length} pytań spoza podstawy?`,
      description: "Soft-delete — zawsze możesz je odtworzyć z backupu.",
      confirmText: "Usuń wszystkie",
      variant: "danger",
    });
    if (!ok) return;
    setBusy("delete-mp");
    try {
      const { data } = await teacherApi.post("/admin/curation/delete-misplaced",
        { ids: misplaced.items.map((x) => x.id) });
      toast.success(`Usunięto ${data.deleted} pytań`);
      setMisplaced(null);
      loadReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd");
    } finally { setBusy(null); }
  };

  const gapFillOne = async (scope, topic) => {
    const { data } = await teacherApi.post("/admin/curation/gap-fill",
      { scope, topic });
    return data;
  };

  const gapFillAll = async () => {
    if (!report) return;
    // Collect all thin/gap canonical cells
    const targets = [];
    for (const row of report.heatmap) {
      for (const cell of row.cells) {
        if (cell.canonical && (cell.status === "gap" || cell.status === "thin")) {
          targets.push({ scope: row.scope, topic: cell.topic, current: cell.count });
        }
      }
    }
    if (!targets.length) {
      toast.info("Wszystkie tematy mają ≥20 pytań 🎉");
      return;
    }
    const ok = await askConfirm({
      title: `Wypełnić luki w ${targets.length} tematach?`,
      description: `Generacja przez Claude 4.6 z sędzią jakości. Każdy temat = jeden request (~$0.02). Łączny koszt: ~$${(targets.length * 0.02).toFixed(2)}. Może potrwać 5-15 min.`,
      confirmText: `Generuj (${targets.length})`,
    });
    if (!ok) return;

    setBusy("gap-fill");
    setGapProgress({ done: 0, total: targets.length, current: null, generated: 0 });
    let genTotal = 0;
    for (let i = 0; i < targets.length; i++) {
      const t = targets[i];
      setGapProgress({ done: i, total: targets.length, current: `${t.scope} · ${t.topic}`, generated: genTotal });
      try {
        const d = await gapFillOne(t.scope, t.topic);
        genTotal += d.generated || 0;
      } catch (e) {
        toast.error(`${t.topic}: ${e.response?.data?.detail || e.message}`);
      }
    }
    setGapProgress(null);
    setBusy(null);
    toast.success(`Wygenerowano ${genTotal} nowych pytań`);
    loadReport();
  };

  const bulkSuggestSynonyms = async () => {
    const ok = await askConfirm({
      title: "Zaproponować synonimy dla WSZYSTKICH otwartych pytań?",
      description: "Claude 4.6 analizuje każde open pytanie i proponuje warianty. Koszt: ~$0.02 per pytanie. Przy 500 pytaniach = ~$10. Możesz zatrzymać w każdej chwili — nie tracisz progresu.",
      confirmText: "Uruchom sweep",
    });
    if (!ok) return;
    setBusy("bulk-syn");
    let totalProcessed = 0, totalWith = 0;
    // Iterate in chunks of 20 until backend reports no remaining eligible.
    try {
      while (true) {
        setSynProgress({ processed: totalProcessed, withSuggestions: totalWith,
                          current: "Analizuję kolejne 20…" });
        const { data } = await teacherApi.post("/admin/curation/bulk-suggest-synonyms",
          { limit: 20, only_new: true });
        totalProcessed += data.processed || 0;
        totalWith += data.with_suggestions || 0;
        if ((data.processed || 0) === 0 || (data.remaining || 0) === 0) break;
      }
      setSynProgress(null);
      toast.success(`Przeanalizowano ${totalProcessed} pytań · propozycje dla ${totalWith}`);
      loadPendingSyn();
    } catch (e) {
      setSynProgress(null);
      toast.error(e.response?.data?.detail || "Błąd sweep");
    } finally { setBusy(null); }
  };

  const loadPendingSyn = async () => {
    try {
      const { data } = await teacherApi.get("/admin/curation/pending-synonyms?limit=500");
      const items = (data.items || []).map((q) => ({
        ...q,
        selection: (q.suggested_synonyms || []).map((s) => ({ text: s, selected: true })),
      }));
      setPendingSyn({ count: data.count, items });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd wczytywania propozycji");
    }
  };

  const toggleSynPick = (qIdx, sIdx) => {
    setPendingSyn((cur) => {
      const items = cur.items.map((it, i) => {
        if (i !== qIdx) return it;
        return {
          ...it,
          selection: it.selection.map((s, j) => j === sIdx ? { ...s, selected: !s.selected } : s),
        };
      });
      return { ...cur, items };
    });
  };

  const applyOne = async (qIdx) => {
    const q = pendingSyn.items[qIdx];
    const picks = q.selection.filter((s) => s.selected).map((s) => s.text);
    try {
      const { data } = await teacherApi.post(`/admin/curation/apply-synonyms/${q.id}`,
        { selected: picks });
      const rejudgedNote = data.rejudged
        ? (data.quality_warning ? " (ostrzeżenie AI odświeżone)" : " (ostrzeżenie AI wyczyszczone)")
        : "";
      if (data.rejudged && !data.quality_warning) addJustFixed(q.id);
      toast.success(`Dodano ${data.applied} synonimów${rejudgedNote}`);
      setPendingSyn((cur) => ({
        ...cur,
        count: cur.count - 1,
        items: cur.items.filter((_, i) => i !== qIdx),
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd");
    }
  };

  const dismissOne = async (qIdx) => {
    const q = pendingSyn.items[qIdx];
    try {
      await teacherApi.post(`/admin/curation/dismiss-synonyms/${q.id}`);
      setPendingSyn((cur) => ({
        ...cur,
        count: cur.count - 1,
        items: cur.items.filter((_, i) => i !== qIdx),
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd");
    }
  };

  const applyAllSelected = async () => {
    if (!pendingSyn?.items?.length) return;
    const ok = await askConfirm({
      title: `Zastosować synonimy dla ${pendingSyn.items.length} pytań?`,
      description: 'Wybrane checkboxami warianty zostaną dołączone do pola „Poprawna odpowiedź" każdego pytania.',
      confirmText: "Zastosuj wszystkie",
    });
    if (!ok) return;
    setBusy("apply-all-syn");
    let count = 0;
    let cleared = 0;
    try {
      for (const q of pendingSyn.items) {
        const picks = q.selection.filter((s) => s.selected).map((s) => s.text);
        try {
          const { data } = await teacherApi.post(`/admin/curation/apply-synonyms/${q.id}`,
            { selected: picks });
          count += data.applied || 0;
          if (data.rejudged && !data.quality_warning) {
            cleared += 1;
            addJustFixed(q.id);
          }
        } catch (e) { /* skip failed */ }
      }
      const clearedNote = cleared > 0
        ? ` — odświeżono ocenę AI, wyczyszczono ${cleared} ostrzeżeń`
        : "";
      toast.success(`Zastosowano synonimy w ${pendingSyn.items.length} pytaniach (${count} nowych wariantów)${clearedNote}`);
      setPendingSyn(null);
      loadReport();
    } catch (e) {
      toast.error("Błąd bulk apply");
    } finally { setBusy(null); }
  };

  if (loading || !report) {
    return (
      <div className="p-8 text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#64748B]" />
        <div className="mt-2 text-sm text-[#64748B]">Ładuję raport pokrycia…</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="curation-tab">
      {/* Header + KPIs */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-black uppercase mb-1">🧪 Audyt bazy pytań</h2>
          <div className="text-sm text-[#64748B]">
            Łącznie: <b>{report.total_questions}</b> pytań ·
            Cel per temat: <b>{report.target_per_topic}</b> ·
            Spoza podstawy: <b className={report.misplaced_total > 0 ? "text-[#B91C1C]" : ""}>{report.misplaced_total}</b>
          </div>
        </div>
        <Button onClick={loadReport} variant="ghost" className="border-2 border-[#0F172A] rounded-md" data-testid="curation-refresh-btn">
          Odśwież
        </Button>
      </div>

      {/* Action bar */}
      <div className="brut border-4 border-[#0F172A] bg-white rounded-md p-4 flex flex-wrap gap-2">
        <Button onClick={doBackup} disabled={!!busy}
          className="brut brut-hover bg-[#0F172A] text-white border-2 border-[#0F172A] font-bold rounded-md"
          data-testid="curation-backup-btn">
          <Database className="w-4 h-4 mr-2" strokeWidth={2.5} />
          1. Backup
        </Button>
        <Button onClick={() => doFixUnits(true)} disabled={!!busy}
          className="brut brut-hover bg-[#DBEAFE] text-[#1E3A8A] border-2 border-[#0F172A] font-bold rounded-md"
          data-testid="curation-fix-units-btn">
          <Ruler className="w-4 h-4 mr-2" strokeWidth={2.5} />
          2. Fix units (ml→cm³, Ksp→Ks)
        </Button>
        <Button onClick={doNormalize} disabled={!!busy}
          className="brut brut-hover bg-[#E9D5FF] text-[#6B21A8] border-2 border-[#0F172A] font-bold rounded-md"
          data-testid="curation-normalize-btn">
          <Wand2 className="w-4 h-4 mr-2" strokeWidth={2.5} />
          3. Normalize topics (Claude)
        </Button>
        <Button onClick={loadMisplaced} disabled={!!busy}
          className="brut brut-hover bg-[#FEF3C7] text-[#92400E] border-2 border-[#0F172A] font-bold rounded-md"
          data-testid="curation-misplaced-btn">
          <Trash2 className="w-4 h-4 mr-2" strokeWidth={2.5} />
          4. Spoza podstawy
        </Button>
        <Button onClick={gapFillAll} disabled={!!busy}
          className="brut brut-hover bg-[#D1FAE5] text-[#065F46] border-2 border-[#0F172A] font-bold rounded-md"
          data-testid="curation-gapfill-all-btn">
          <PlayCircle className="w-4 h-4 mr-2" strokeWidth={2.5} />
          5. Wypełnij wszystkie luki
        </Button>
        <Button onClick={bulkSuggestSynonyms} disabled={!!busy}
          className="brut brut-hover bg-[#F3E8FF] text-[#6B21A8] border-2 border-[#0F172A] font-bold rounded-md"
          data-testid="curation-bulk-syn-btn">
          <Sparkles className="w-4 h-4 mr-2" strokeWidth={2.5} />
          6. Synonimy (bulk, AI)
        </Button>
        <Button onClick={loadPendingSyn} disabled={!!busy}
          variant="ghost"
          className="border-2 border-[#0F172A] rounded-md font-bold text-[#6B21A8]"
          data-testid="curation-load-pending-syn-btn"
          title="Wczytaj oczekujące propozycje AI">
          Wczytaj oczekujące
        </Button>
      </div>

      {synProgress && (
        <div className="brut border-4 border-[#0F172A] bg-[#F3E8FF] rounded-md p-4" data-testid="syn-progress">
          <div className="font-black uppercase text-sm mb-2 text-[#6B21A8]">Bulk synonym sweep…</div>
          <div className="text-sm text-[#6B21A8]">
            Przeanalizowano: <b>{synProgress.processed}</b> · Z propozycjami: <b>{synProgress.withSuggestions}</b>
          </div>
        </div>
      )}

      {/* TEST topic cleanup — orphaned pytest fixtures */}
      {testTopics && testTopics.topics.length > 0 && (
        <div
          className="brut border-4 border-[#0F172A] bg-[#FEE2E2] rounded-md p-4"
          data-testid="test-topics-section"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
            <div>
              <div className="font-black uppercase text-sm text-[#991B1B]">
                🧹 Pytania testowe (TEST_*)
              </div>
              <div className="text-sm text-[#991B1B] mt-1">
                W bazie są {testTopics.total_questions} pytania w{" "}
                {testTopics.topics.length} tematach pasujących do wzorca <code className="font-mono">TEST*</code>.
                To zazwyczaj artefakty z testów automatycznych (pytests), które nie wyczyściły się po sobie.
                Można je bezpiecznie usunąć — żaden nauczyciel ich nie użyje w prawdziwej kartkówce.
              </div>
            </div>
            <Button
              onClick={() => purgeTestTopics(null)}
              disabled={!!busy}
              className="brut brut-hover bg-[#B91C1C] hover:bg-[#B91C1C] text-white border-2 border-[#0F172A] font-black rounded-md"
              data-testid="purge-all-test-topics-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" strokeWidth={2.5} />
              Usuń wszystkie ({testTopics.total_questions})
            </Button>
          </div>
          <details className="text-xs">
            <summary
              className="cursor-pointer font-bold text-[#991B1B]"
              data-testid="test-topics-list-toggle"
            >
              Pokaż listę tematów (klik, by usunąć pojedynczo)
            </summary>
            <div className="mt-2 max-h-72 overflow-auto border-2 border-[#0F172A] rounded bg-white p-2 space-y-1">
              {testTopics.topics.map((t) => (
                <div
                  key={t.topic}
                  className="flex items-center justify-between gap-2 py-1 px-2 hover:bg-[#F8FAFC] rounded"
                  data-testid={`test-topic-row-${t.topic}`}
                >
                  <div className="flex-1 min-w-0 truncate">
                    <span className="font-mono font-bold">{t.topic}</span>
                    <span className="text-[#64748B] ml-2">({t.count} pytań)</span>
                    {t.scopes.length > 0 && (
                      <span className="text-[#94A3B8] ml-2 italic">{t.scopes.join(", ")}</span>
                    )}
                  </div>
                  <button
                    onClick={() => purgeTestTopics([t.topic])}
                    disabled={!!busy}
                    className="brut bg-white border-2 border-[#0F172A] rounded px-2 py-0.5 text-[11px] font-bold hover:bg-[#FEE2E2]"
                    data-testid={`purge-test-topic-${t.topic}`}
                  >
                    Usuń
                  </button>
                </div>
              ))}
            </div>
          </details>
        </div>
      )}

      {pendingSyn && pendingSyn.items.length > 0 && (
        <div className="brut border-4 border-[#6B21A8] bg-[#F3E8FF] rounded-md p-4" data-testid="pending-syn-panel">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="font-black uppercase text-sm text-[#6B21A8]">
              ✨ Do zatwierdzenia — synonimy ({pendingSyn.count})
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setPendingSyn(null)} variant="ghost" size="sm">
                Zamknij
              </Button>
              <Button onClick={applyAllSelected} disabled={busy === "apply-all-syn"}
                className="brut bg-[#6B21A8] text-white font-bold rounded-md" size="sm"
                data-testid="apply-all-syn-btn">
                Zastosuj wszystkie zaznaczone
              </Button>
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto space-y-2">
            {pendingSyn.items.map((q, qi) => (
              <div key={q.id} className="bg-white border-2 border-[#6B21A8] rounded-md p-3"
                   data-testid={`pending-syn-${qi}`}>
                <div className="text-xs text-[#64748B] mb-1">
                  [{q.scope}] {q.topic || "—"}
                </div>
                <div className="text-sm font-bold text-[#0F172A] mb-1 line-clamp-2">
                  {q.text}
                </div>
                <div className="text-xs text-[#64748B] mb-2">
                  Klucz: <b>{q.correct}</b>
                </div>
                <div className="flex flex-wrap gap-1 mb-2">
                  {q.selection.map((s, si) => (
                    <label key={si}
                           className={`text-xs px-2 py-1 rounded border-2 cursor-pointer transition ${
                             s.selected ? "bg-[#F3E8FF] border-[#6B21A8] text-[#6B21A8]"
                                        : "bg-white border-[#CBD5E1] text-[#64748B]"
                           }`}
                           data-testid={`pending-syn-${qi}-pick-${si}`}>
                      <input type="checkbox" checked={s.selected}
                             onChange={() => toggleSynPick(qi, si)}
                             className="mr-1 align-middle" />
                      {s.text}
                    </label>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => applyOne(qi)} size="sm"
                          className="brut bg-[#6B21A8] text-white font-bold rounded-md text-xs h-7"
                          data-testid={`pending-syn-${qi}-apply`}>
                    Zastosuj
                  </Button>
                  <Button onClick={() => dismissOne(qi)} variant="ghost" size="sm"
                          className="text-xs h-7 text-[#7F1D1D]"
                          data-testid={`pending-syn-${qi}-dismiss`}>
                    Odrzuć
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {gapProgress && (
        <div className="brut border-4 border-[#0F172A] bg-[#FEF08A] rounded-md p-4" data-testid="curation-progress">
          <div className="font-black uppercase text-sm mb-2">Generacja…</div>
          <div className="text-sm">
            {gapProgress.done} / {gapProgress.total} · Aktualnie: <b>{gapProgress.current || "…"}</b> · Nowych: {gapProgress.generated}
          </div>
          <div className="mt-2 h-2 bg-white border-2 border-[#0F172A] rounded-md overflow-hidden">
            <div className="h-full bg-[#0F172A]" style={{ width: `${(gapProgress.done / Math.max(gapProgress.total, 1)) * 100}%` }} />
          </div>
        </div>
      )}

      {misplaced && (
        <div className="brut border-4 border-[#B91C1C] bg-[#FEF2F2] rounded-md p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="font-black uppercase text-sm text-[#7F1D1D]">
              Pytania spoza podstawy programowej ({misplaced.count})
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setMisplaced(null)} variant="ghost" size="sm">Zamknij</Button>
              {misplaced.count > 0 && (
                <Button onClick={deleteMisplacedBatch} disabled={busy === "delete-mp"}
                  className="brut bg-[#EF4444] text-white font-bold rounded-md" size="sm"
                  data-testid="delete-misplaced-batch-btn">
                  Usuń wszystkie ({misplaced.items.length})
                </Button>
              )}
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto space-y-1 text-xs">
            {misplaced.items.slice(0, 100).map((q) => (
              <div key={q.id} className="bg-white p-2 rounded-md border border-[#FCA5A5]">
                <span className="font-bold text-[#7F1D1D]">[{q.scope}] {q.topic || "(brak)"}:</span> {q.text?.slice(0, 120)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Heatmap */}
      <div className="space-y-4">
        {report.heatmap.map((row) => (
          <div key={row.scope} className="brut border-4 border-[#0F172A] bg-white rounded-md p-4"
               data-testid={`heatmap-scope-${row.scope}`}>
            <div className="font-black uppercase text-sm mb-3 tracking-wider">{row.label}</div>
            <div className="flex flex-wrap gap-2">
              {row.cells.map((cell, i) => (
                <div
                  key={i}
                  className={`brut border-2 border-[#0F172A] rounded-md px-3 py-1.5 text-xs font-bold ${STATUS_COLOR[cell.status] || ""}`}
                  title={`${cell.canonical ? "kanoniczny" : "spoza programu"} · ${cell.warnings} ostrzeżeń`}
                  data-testid={`cell-${row.scope}-${i}`}
                >
                  <span>{cell.topic}</span>
                  <span className="ml-2 tabular-nums">{cell.count}</span>
                  <span className="ml-2 text-[10px] opacity-70 uppercase">{STATUS_LABEL[cell.status]}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Duplicate clusters (raw text similarity) */}
      {report.duplicate_clusters?.length > 0 && (
        <div className="brut border-4 border-[#0F172A] bg-white rounded-md p-4">
          <button
            onClick={() => setShowDupes((x) => !x)}
            className="w-full flex items-center justify-between text-left font-black uppercase text-sm"
            data-testid="toggle-duplicates-btn"
          >
            <span>Podobne tematy do sprawdzenia ({report.duplicate_clusters.length} klastrów)</span>
            {showDupes ? <ChevronUp /> : <ChevronDown />}
          </button>
          {showDupes && (
            <div className="mt-3 space-y-2 text-sm">
              {report.duplicate_clusters.map((cluster, i) => (
                <div key={i} className="bg-[#F8FAFC] border-2 border-[#0F172A] rounded-md p-2">
                  {cluster.join(" ≈ ")}
                </div>
              ))}
              <div className="text-xs text-[#64748B] pt-2">
                Wskazówka: puść &bdquo;Normalize topics&rdquo; &mdash; Claude zaproponuje kanoniczne nazwy.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
